/**
 * @file src/utils/index.js
 * @author Ryan Rossiter
 *         Paul, paul@distributive.network
 * @date Feb 2020
 *       April 2023
 *
 * Place to put little JS utilities. If they are more than a few lines, please `require` and `export` 
 * them instead of making this monolithic.
 * @module dcp/utils
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
/* global dcpConfig */ // eslint-disable-line no-redeclare
// @ts-check
'use strict';

const { DCPError } = require('dcp/common/dcp-error');
const { assert } = require('dcp/common/dcp-assert');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const dcpEnv = require('dcp/common/dcp-env');

Object.assign(exports, require('./at-exit'));
module.exports.paramUtils = require('./assert-params');
module.exports.requestFields = require('./request-fields.js');
module.exports.serialize = require('./serialize');
module.exports.prefs= require('./user-prefs');
Object.assign(exports, require('./web-format-date'));
Object.assign(exports, require('./confirm-prompt'));
Object.assign(exports, require('./content-encoding'));
Object.assign(exports, require('./submit-post'));
exports.schedulerConstants = require('dcp/common/scheduler-constants');

/** @typedef {import('dcp/dcp-client/worker/supervisor2/slice2').Slice} Slice */
/** @typedef {import('dcp/dcp-client/worker/supervisor2/sandbox2').Sandbox} Sandbox */

/** Unique symbol to identify an 'always' boxed string. */
const alwaysSymbol = dcpEnv.platform !== 'pythonmonkey' ?  Symbol('always') : {};
exports.alwaysSymbol = alwaysSymbol;

 /**
  * Writes object properties into another object matching shape.
  * For example: if obj = { a: { b: 1, c: 2}}
  * and             source = { a: {c: 0, d: 1}}
  * then obj will be updated to { a: { b: 1, c: 0, d: 1 } }
  * compare this to Object.assign which gives { a: { c: 0, d: 1 } }
  */
const setObjProps = module.exports.setObjProps = (obj, source) => {
  for (let p in source) {
    if (typeof source[p] === 'object') setObjProps(obj[p], source[p]);
    else obj[p] = source[p];
  }
}


/**
 * Generates a new random opaqueId i.e. a 22-character base64 string.
 * Used for job and slice ids.
 *
 * @todo    either fix the naming or break worker id and other id details out separately
 */
module.exports.generateOpaqueId = function utils$$generateOpaqueId()
{
  if (!utils$$generateOpaqueId.nanoid)
  {
    const nanoidModule = require('nanoid');
    utils$$generateOpaqueId.nanoid = nanoidModule.customAlphabet(exports.schedulerConstants.workerIdAlphabet,
                                                                 exports.schedulerConstants.workerIdLength);
  }

  return utils$$generateOpaqueId.nanoid();
}

/**
 * Generate a new random worker id. Currently all opaqueIds are the same, but our constants' naming
 * implies otherwise and could plausibly change in the future.
 */
module.exports.generateWorkerId = module.exports.generateOpaqueId;

/**
 * Accepts an object and a list of properties to apply to the object to retreive a final value.
 * E.g.
 *  object = { a: { b: { c: 4 } } }
 *  listOfProperties = ["a", "b", "c"]
 *  return = 4
 */
module.exports.getNestedFromListOfProperties = function (object, listOfProperties) {
  return listOfProperties.reduce((o, k) => {
    if (typeof o !== 'object') return undefined;
    return o[k];
  }, object);
}

/**
 * Accepts an object and a dot-separated property name to retrieve from the object.
 * E.g.
 *  object = { a: { b: { c: 4 } } }
 *  property = 'a.b.c'
 *  return = 4
 *
 * @param {object} object 
 * @param {string} property 
 */
module.exports.getNestedProperty = function (object, property) {
  if (typeof property !== 'string') return undefined;
  return module.exports.getNestedFromListOfProperties(object, property.split("."));
}

/**
 * Accepts an object and a list of properties and a value and mutates the object
 * to have the specified value at the location denoted by the list of properties.
 * Similar to getNestedFromListOfProperties, but sets instead of gets.
 */
module.exports.setNestedFromListOfProperties = function (object, listOfProperties, value) {
  if(!listOfProperties.length || listOfProperties.length < 1) {
    throw new Error("listOfProperties must be an array of length >= 1");
  }
  const indexOfLastProp = listOfProperties.length - 1;
  const pathToParent = listOfProperties.slice(0, indexOfLastProp);
  const parent = module.exports.getNestedFromListOfProperties(object, pathToParent);
  if(!parent) {
    throw new Error("Could not find value at:", pathToParent, "in object:", object);
  }
  const lastProperty = listOfProperties[indexOfLastProp];
  parent[lastProperty] = value;
}

/**
 * Block the event loop for a specified time
 * @milliseconds        the number of milliseconds to wait (integer)
 */
exports.msleep = function utils$$msleep(milliseconds) {
  try
  {
    let sab = new SharedArrayBuffer(4);
    let int32 = new Int32Array(sab);
    Atomics.wait(int32, 0, 0, milliseconds);
  }
  catch(error)
  {
    console.error('Cannot msleep;', error);
  }
}

/**
 * Block the event loop for a specified time
 * @seconds     the number of seconds to wait (float)
 */
exports.sleep = function utils$$sleep(seconds) {
  return exports.msleep(seconds * 1000);
}

/** 
 * Resolve a promise after a specified time.
 * 
 * @param {number} ms      the number of milliseconds after which to resolve the promise.
 * @returns Promise with an extra property, intr(). Calling this function causes the promise
 *          to resolve immediately. If the promise was interrupted, it will resolve with false;
 *          otherwise, true.
 */
exports.a$sleepMs = function a$sleepMs(ms)
{
  var interrupt, timerHnd;
  
  const p$ret = new Promise((resolve, reject) => {
    var resolved = false;
    timerHnd = setTimeout(function __a$sleepMs() { resolved=true; resolve(false) }, ms);
    function a$sleepMs_intr()
    {
      clearTimeout(timerHnd);
      if (!resolved)
        resolve(true);
    }
      
    interrupt = a$sleepMs_intr;
  });

  p$ret.intr = () => interrupt();
  p$ret.unref = () => {
    timerHnd.unref();
    return p$ret;
  }
  p$ret.ref = () => {
    timerHnd.ref();
    return p$ret;
  }
  return p$ret;
}

/** 
 * @see: a$sleepMs
 * @param {number} ms      the number of milliseconds after which to resolve the promise.
 */
exports.a$sleep = function a$sleep(seconds) {
  return exports.a$sleepMs(seconds * 1000);
}

/** 
 * Returns the number of millisecond in a time expression.
 * @param s {number}    The number of seconds
 * @returns {number}
 */
/**
 * @param s {string|number} - A complex time expression using m, w, d, s, h. '10d 6h 1s' means 10 days, 6 hours, and 1 second.
 */
exports.ms = function utils$$ms(s)
{
  let ms = 0;
  
  if (typeof s === 'number')
    return s * 1000;

  assert(typeof s === 'string');
  
  for (let expr of s.match(/[0-9.]+[smhdw]/g))
  {
    let unit = expr.slice(-1);
    let value = +expr.slice(0, -1);

    switch(unit)
    {
      case 's': {
        ms += value * 1000;
        break;
      }
      case 'm': {
        ms += value * 1000 * 60;
        break;
      }
      case 'h': {
        ms += value * 1000 * 60 * 60;
        break;
      }
      case 'd': {
        ms += value * 1000 * 60 * 60 * 24;
        break;
      }
      case 'w': {
        ms += value * 1000 * 60 * 60 * 24 * 7;
        break;
      }
      default: {
        throw new Error(`invalid time unit ${unit}`);
      }
    }
  }

  return ms;
}

/**
 * @constructor
 * The Backoff class makes series of numbers useful for building network backoff algorithsm.
 *
 * @param {Object}   params - how to configure the backoff; numeric properties as follows:
 *                    - mult    How much to multiply the backoff by; linear scaling, smaller -> more gradual increase
 *                    - base    The exponent base that we raise by to create our series
 *                    - max     The largest number that can be returned; acts as a clamp
 * @param {number}  [maxOverride] - if specified, overrides params.max.
 *
 * @returns an object with a method `next` which returns the next value in the series.
 */
exports.Backoff = function Backoff(params, maxOverride)
{
  var { mult, base, max } = params;
  if (maxOverride !== undefined)
    max = maxOverride;
  this.reset();
  this.next = () => Math.min(max, mult * (Math.pow(base, this.n++) - 1));
}

/**
 * Reset the backoff; next backoff amount will be 0 again
 */
exports.Backoff.prototype.reset = function Backoff$$reset()
{
  this.n = 0;
}

/* Add fancy output for console.log in Node.js */
if (dcpEnv.platform === 'nodejs')
{
  const inspect = requireNative('util').inspect.custom;
  exports.Backoff.prototype[inspect] = function Backoff$$next() {
    const next = this.next();
    this.n--;
    return `[Object Backoff <next: ${next}>]`;
  };
}

/**
 * @param n {number}    this number is returned, divided by 100 .
 */
/**
 * @param n {string}    this string is converted to a number and returned; it is divided by 100 if it ends in %.
 */
/**
 * Returns a percentage as a number.
 * @param n {number|string}    this number is returned
 * @returns {number}
 */
exports.pct = function utils$$pct(n)
{
  if (typeof n === 'number')
    return n / 100;

  if (n.match(/%$/))
    return +n / 100;

  return +n;
}

/**
 * Coerce human-written or registry-provided values into Boolean in a sensisble way.
 */
exports.booley = function utils$$booley(check)
{
  switch (typeof check)
  {
    case 'undefined':
      return false;
    case 'string':
      return check && (check !== 'false');
    case 'boolean':
      return check;
    case 'number':
    case 'object':
      return Boolean(check);
    default:
      throw new Error(`can't coerce ${typeof check} to booley`);
  }
}

if (dcpEnv.platform === 'nodejs')
  exports.useColour = requireNative ? requireNative('tty').isatty(0) || process.env.FORCE_COLOR : false;
else
  exports.useColour = false;

/** 
 * Factory function which constructs an error message relating to version mismatches
 * @param {string}     oldThingLabel           The name of the thing that is too old
 * @param {string}     upgradeThingLabel       [optional] The name of the thing that needs to be upgraded to fix that; if
 *                                             unspecified, we use oldThingLabel.
 * @param {string}     newThingLabel           What the thing is that is that is newer than oldThingLabel
 * @param {string}     minimumVersion          The minimum version of the old thing that the new thing supports. 
 *                                             Obstensibibly semver, but unparsed.
 * @param {string}     code                    [optional] A code property to add to the return value
 *
 * @returns instance of Error
 */
exports.versionError = function dcpClient$$versionError(oldThingLabel, upgradeThingLabel, newThingLabel, minimumVersion, code)
{
  function repeat(what, len) {
      let out = '';
      while (len--)
        out += what;
      return out;
    }

  function bold(string) {
    if (require('dcp/common/dcp-env').platform === 'nodejs') {
      const chalk = new (requireNative('chalk').Instance)({level: exports.useColour ? 1 : 0});

      return chalk.yellow(string);
    }

    return string;
  }
  
  var mainMessage = `${oldThingLabel} is too old; ${newThingLabel} needs version ${minimumVersion}`;
  var upgradeMessage = bold(`Please upgrade ${upgradeThingLabel || oldThingLabel}${repeat(" ", mainMessage.length - 15 - upgradeThingLabel.length)}`);
  var error = new Error(`
****${repeat('*', mainMessage.length)}****
*** ${repeat(' ', mainMessage.length)} ***
*** ${mainMessage} ***
*** ${upgradeMessage} ***
*** ${repeat(' ', mainMessage.length)} ***
****${repeat('*', mainMessage.length)}****
`);

  if (code)
    error.code = code;
  return error;
}

/**
 * Detect whether value is from the output of job-values:serializeJobValue.
 * @param {*} value
 * @returns {boolean}
 */
exports.isFromSerializeJobValue = function utils$$isFromSerializeJobValue(value)
{
  return value.hasOwnProperty('string') && value.hasOwnProperty('method') && value.hasOwnProperty('MIMEType') && Object.keys(value).length === 3;
}

/**
 *  JSON Serialization
 *    stringify  -- ignore cycles
 *    dumpJSON   -- safely wrapped stringify with header
 *    dumpObject -- Apply dumpJSON to every [key, value] of Object.entries(theObject)
 *********************************************************************************************/

/**
 * Quck and dirty JSON serialization that ignores cycles.
 *
 * @param {*}      theAnything            - entity to be serialized.
 * @param {number} [truncationLength=512] - number of string elements to return.
 * @param {number} [space=0]              - # of spaces to indent (0 <= space <= 10).
 * @returns {string}
 */
exports.stringify = function _stringify(theAnything, truncationLength = 512, space = 2) {
  let cache = [];
  const str = JSON.stringify(theAnything, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.includes(value)) return;
      cache.push(value);
    }
    return value;
  }, space);
  cache = null;
  if (str && truncationLength > 0)
    return str.slice(0, truncationLength);
  return str;
}

/**
 * Calls truncated JSON.stringify on theAnything.
 * @param {*}      theAnything
 * @param {string} [header='dumpJSON']
 * @param {number} [truncationLength=512]
 */
exports.dumpJSON = function utils$$dumpJSON(theAnything, header = 'dumpJSON: ', truncationLength = 512) {
  if (theAnything) {
    const strV = exports.stringify(theAnything, truncationLength);
    if (strV) console.log(`${header}: ${String(strV)}`);
    else console.log(`${header}:`, theAnything);
  } else console.log(`${header}:`, theAnything);
}

/**
 * Iterates over all property [key, value]-pairs of theObject and call truncated JSON.stringify on property values.
 * @param {object} theObject
 * @param {string} [header='dumpObject']
 * @param {number} [truncationLength=512]
 */
exports.dumpObject = function utils$$dumpObject(theObject, header = 'dumpObject: ', truncationLength = 512, dumpKeys = true) {
  if (theObject) {
    if (dumpKeys) console.log(`${header}: dump the keys`, Object.keys(theObject));
    console.log(`${header}: dump the key-value entries...`);
    console.group();
    for (const [key, value] of Object.entries(theObject))
      exports.dumpJSON(value, `${header}.${key}`, truncationLength);
    console.groupEnd();
  }
}

/**
 *  Compressed Representations and Maps
 *    toJobMap
 *    compressSandboxes
 *    compressSlices
 *    compressJobMap
 *    compressJobArray
 *    compressJobValue
 *    compressJobEntry
 *    compressEnhancedJobEntry
 *    truncateAddress
 *    compressRange
 *    compressEnhancedRange
 *********************************************************************************************/

/**
 * @param {object[]} jobArray
 * @param {function} functor
 * @returns {object}
 */
exports.toJobMap =  function utils$$toJobMap(jobArray, functor) {
  const jobMap = {};
  for (const x of jobArray) {
    if (!jobMap[x.jobId]) jobMap[x.jobId] = [functor(x)];
    else jobMap[x.jobId].push(functor(x));
  }
  return jobMap;
}

/**
 * @param {Sandbox[]} sandboxArray
 * @param {number}    [digits=-1]
 * @returns {string}
 */
exports.compressSandboxes =  function utils$$compressSandboxes(sandboxArray, digits = -1) {
  const jobSandboxMap = exports.toJobMap(sandboxArray, sbx => sbx.id);
  return exports.compressJobMap(jobSandboxMap, digits);
}

/**
 * @param {Slice[]} sliceArray
 * @param {number}  [digits=-1]
 * @returns {string}
 */
exports.compressSlices =  function utils$$compressSlices(sliceArray, digits = -1) {
  const jobSliceMap = exports.toJobMap(sliceArray, slice => slice.sliceNumber);
  return exports.compressJobMap(jobSliceMap, digits);
}

/**
 * @param {object}  jobMap
 * @param {number}  [digits=-1]
 * @param {function} [functor]
 * @returns {string}
 */
exports.compressJobMap =  function utils$$compressJobMap(jobMap, digits = -1, functor) {
  if (functor)
  {
    jobMap = { ...jobMap };
    for (const job in jobMap)
      jobMap[job] = jobMap[job].map((x) => functor(x));
  }
  return exports.compressJobArray(Object.entries(jobMap), digits);
}

/**
 * @param {object[]} jobArray
 * @param {number}   [digits=-1]
 * @returns {string}
 */
exports.compressJobArray = function utils$$compressJobArray(jobArray, digits = -1) {
  let output = '';
  for (let k = 0; k < jobArray.length; k++) {
    output += exports.compressJobValue(jobArray[k], digits);
  }
  return output;
}

/**
 * @param {object|object[]} jobValue
 * @param {number}          [digits=-1]
 * @returns {string}
 */
exports.compressJobValue =  function utils$$compressJobValue(jobValue, digits = -1) {
  if (jobValue.job && jobValue.slices)
    return exports.compressJobEntry(jobValue.job, jobValue.slices, digits);
  if (jobValue.length === 2)
    return exports.compressJobEntry(jobValue[0], jobValue[1], digits);
  return 'nada';
}

/**
 * @param {*}             jobId
 * @param {Array<number>} sliceNumbers
 * @param {number}        [digits=-1]
 * @returns {string}
 */
exports.compressJobEntry =  function utils$$compressJobEntry(jobId, sliceNumbers, digits = -1) {
  return `${exports.truncateAddress(jobId, digits)}:[${exports.compressRange(sliceNumbers)}]:`;
}

exports.compressEnhancedJobEntry =  function utils$$compressEnhancedJobEntry(job, slices, digits = -1) {
  return `${job.id}.${exports.truncateAddress(job.opaqueId, digits)}:[${exports.compressEnhancedRange(slices)}]:`;
}

/**
 * @param {*}      address
 * @param {number} [digits=-1]
 * @returns {string}
 */
exports.truncateAddress = function utils$$truncateAddress(address, digits = -1) {
  let value = address.toString();
  if (digits < 0) return value.startsWith('0x') ? value.substring(2) : value;
  return value.startsWith('0x') ? value.substring(2, 2 + digits) : value.substring(0, digits);
}

/**
 * Input [2, 3, 4, 7, 5, 8, 9, 13, 14, 15] returns '2-5,7-9,13-15'
 * Input [2, 3, 4, 7, 5, 8, 9, 13]         returns '2-5,7-9,13'
 * Input [2, 3, 4, 7, 5, 4, 4, 8, 9]       returns '2-4,4,4-5,7-9'
 * @param {Array<number>} numberArray
 * @returns {string}
 */
exports.compressRange = function utils$$compressRange(numberArray) {
  if (!(numberArray?.length > 0))
    return '';
  numberArray = [...numberArray];
  numberArray.sort((x, y) => x - y); // increasing...
  let start = numberArray[0];
  let output = `${start}`;
  for (let k = 1; k < numberArray.length; k++) {
    assert(typeof numberArray[k] === 'number' || numberArray[k] && numberArray[k].constructor.name === 'Number');
    if (numberArray[k] - numberArray[k - 1] !== 1) {
      output += (numberArray[k - 1] > start) ? `-${numberArray[k - 1]},` : ',';
      start = numberArray[k];
      output += `${start}`;
    } else if (k === numberArray.length - 1) {
      output += `-${numberArray[k]}`;
    }
  }
  return output;
}

exports.compressEnhancedRange = function utils$$compressEnhancedRange(slices) {
  assert(slices && Array.isArray(slices));
  slices = [...slices];
  slices.sort((x, y) => x.sliceNumber - y.sliceNumber); // increasing...
  let start = slices[0];
  let output = fragment(start);
  for (let k = 1; k < slices.length; k++) {
    if (slices[k].sliceNumber - slices[k - 1].sliceNumber !== 1) {
      output += (slices[k - 1].sliceNumber > start.sliceNumber) ? `-${fragment(slices[k - 1])},` : ',';
      start = slices[k];
      output += `${fragment(start)}`;
    } else if (k === slices.length - 1) {
      output += `-${fragment(slices[k])}`;
    }
  }
  return output;
}

function fragment(slice) {
  return `${slice.sliceNumber}.${slice.isEstimationSlice}.${slice.isLongSlice}`;
}

/**
 *  categorizeContentType
 *    Analyze contentType (cf. https://www.w3.org/Protocols/rfc1341/4_Content-Type.html) and
 *    determine whether it represents JSON, KVIN, binary or text.
 *********************************************************************************************/

function normalizeCharset(charset)
{
  const checkQuote = (c) => { return (c === "'" || c === '"' || c === "`"); }
  if (checkQuote(charset[0]) && checkQuote(charset[charset.length - 1]))
    charset = charset.slice(1, charset.length - 1);
  // We don't diagnose 1-sided quotes -- these are malformed.
  return charset.toLowerCase();
}

/**
 * Transforms "text/plain;charset=utf8" to { type: "text/plain", topType: "text", charset: "utf8" }
 * @param {string} contentType - getResponseHeader('content-type')
 * @returns {{ type: string, topType: string, charset: string }}
 */
function parseContentType(contentType)
{
  if (!contentType)
    return { type: "text/plain", topType: "text", charset: "utf8" };
  // Eliminate any whitespace
  contentType = contentType.replace(/\s/g, '');
  // Parse contentType into tokens.
  // https://www.w3.org/Protocols/rfc1341/4_Content-Type.html
  // Content-Type := type "/" subtype *[";" parameter]
  // parameter := attribute "=" value
  const ctArray = contentType.split(';');
  const type = ctArray[0];
  const topType = type.split('/')[0];
  let charset;
  if (ctArray.length > 1)
  {
    const paramArray = ctArray[1].split('=');
    if (paramArray.length > 1 && paramArray[0].toLowerCase() === 'charset')
      charset = normalizeCharset(paramArray[1]);
  } else if (topType === 'text')
    charset = 'us-ascii'; // https://www.w3.org/Protocols/rfc1341/7_1_Text.html

  return { type, topType, charset };
}
/**
 * Transforms:
 *   "text/plain;charset=utf8"    --> { isJson: false, isKVIN: false, isBinary: false, isText: true }
 *   "application/JSON"           --> { isJson: true,  isKVIN: false, isBinary: false, isText: false }
 *   "application/x-kvin"         --> { isJson: false, isKVIN: true,  isBinary: false, isText: false }
 *   "application/octet-stream"   --> { isJson: false, isKVIN: false, isBinary: true,  isText: false }
 *   "application/javascript"     --> { isJson: false, isKVIN: false, isBinary: false, isText: false }
 * @param {string} contentType    - getResponseHeader('content-type')
 * @param {string} [overrideType] - when overrideType === "JSON", return { isJSON: true, isKVIN: false, isBinary: false, isText: false };
 * @returns {{ isJSON: boolean, isKVIN: boolean, isBinary: boolean, isText: boolean }}
 */
exports.categorizeContentType = function justFetch$categorizeContentType(contentType, overrideType)
{
  if (overrideType === 'JSON')
    return { isJSON: true, isKVIN: false, isBinary: false, isText: false };

  const { type, topType, charset } = parseContentType(contentType);

  const isJSON = (type === 'application/json');
  const isKVIN = (type === 'application/x-kvin');
  const isJS   = (type === 'application/javascript');
  const isText = (topType === 'text'
    && (charset === 'utf-8' || charset === 'utf8'            // utf8
      || charset === 'us-ascii' || charset === 'ascii'       // 7bit
      || charset === 'iso-8859-1' || charset === 'latin1')); // 8bit
  const isBinary = !(isJSON || isKVIN || isText || isJS);

  return { isJSON, isKVIN, isBinary, isText };
}

/**
 *  shuffle
 *  hashGeneration
 *********************************************************************************************/

/**
 * Randomly shuffle array.
 * @param {Array<any>} array
 */
exports.shuffle = function utils$$shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
}

/**
 * Return a new array composed only of unique elements of arr
 * @param {Array} arr
 * @returns {Array}
 */
exports.uniq = function utils$$uniq(array)
{
  return array.filter((val, idx, arr) => arr.indexOf(val) === idx);
}

exports.hashGeneration = function utils$$hashGeneration(object) {
  const { sha256 } = require('dcp/dcp-client/wallet/keystore')._internalEth.util;
  return sha256(Buffer.from(JSON.stringify(object)));
}

/**
 *  CG service error handling types and helpers.
 *********************************************************************************************/

/**
 * @typedef {object} apiClientType
 * @property {boolean} success
 * @property {*} [payload]
 * @property {DCPError} [error]
 */

/**
 * @typedef {object} apiServiceType
 * @property {boolean} success
 * @property {*} [payload]
 * @property {string} [message]
 * @property {string} [stack]
 * @property {string} [code]
 */

/**
 * @param {apiServiceType} payload 
 * @returns {apiClientType}
 */
exports.reconstructServiceError = function utils$$reconstructServiceError(payload) {
  assert(!payload.success);
  let ex = new DCPError(payload.message);
  ex.stack = dcpConfig.worker.allowConsoleAccess ? payload.stack : '';
  if (payload.code) ex.code = payload.code;
  return exports._clientError(ex);
}

/**
 * @param {DCPError} ex
 * @returns {apiClientType}
 */
exports._clientError = function utils$$_clientError(ex) {
  if (dcpConfig.worker.allowConsoleAccess) console.error(ex);
  return { success: false, error: ex };
}
/**
 * @param {string} message
 * @returns {apiClientType}
 */
exports.clientError = function utils$$clientError(message) {
  const ex = new DCPError(message);
  return exports._clientError(ex);
}

/**
 * @param {DCPError} ex
 * @returns {apiServiceType}
 */
exports._serviceError = function utils$$_serviceError(ex) {
  return { success: false, message: ex.message, stack: ex.stack, code: ex.code };
}
/**
 * @param {string} message
 * @param {string|object} codeEx
 * @returns {apiServiceType}
 */
exports.serviceError = function utils$$serviceError(message, codeEx) {
  const ex = new DCPError(message, codeEx);
  return exports._serviceError(ex);
}

exports.chalk = function utils$$chalk()
{
  return new (require('chalk').Instance)({level: exports.useColour ? 1 : 0});
}

/**
 *  exports
 *********************************************************************************************/

if (require('dcp/common/dcp-env').platform === 'nodejs') {
  Object.assign(exports, require('./tmpfiles'));
}
module.exports.encodeDataURI = require('./encodeDataURI').encodeDataURI;

Object.assign(exports, require('./readln'));
Object.assign(exports, require('./sh'));
Object.assign(exports, require('./eventUtils'));
Object.assign(exports, require('./obj-merge'));
Object.assign(exports, require('./make-data-uri'));
Object.assign(exports, require('./just-fetch'));
Object.assign(exports, require('./inventory'));
Object.assign(exports, require('./fetch-keystore'));
Object.assign(exports, require('./fetch-uri'));
Object.assign(exports, require('./ring-buffer'));
Object.assign(exports, require('./constant-groups'));
Object.assign(exports, require('./value-collection'));
Object.assign(exports, require('./strings'));
Object.assign(exports, require('./http'));
Object.assign(exports, require('./server-time'));

exports.hash = require('../common/hash');
exports.concurrency = require('../common/concurrency');
